// RoutingGuideForm.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";

const RoutingGuideForm = ({
  open,
  onClose,
  onSubmit,
  divisions,
  commodityCodes,
}) => {
  const Division = [
    { id: 1000,value: "Neiman Marcus Stores", label: "Neiman Marcus Stores" },
    { id: 2000,value: "Neiman Marcus Direct", label: "Neiman Marcus Direct" },
    { id: 3000,value: "Bergdorf Goodman", label: "Bergdorf Goodman" },
    { id: 4000,value: "Last Call", label: "Last Call" },
  ];
  const [formData, setFormData] = useState({
    guide_name: "",
    division_id: "",
    supplier_site_id: '',
    vendor_name: "",
    commodity_code_id: "",
    freight_terms: "",
    current_version: "",
    prev_version: "",
    publish_date: "",
    effective_date: "",
    expiration_date: "",
    comments: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear error when field is filled
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      "guide_name",
      "division_id",
      "commodity_code_id",
      "freight_terms",
      "current_version",
      "publish_date",
      "effective_date",
      "expiration_date",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
      }
    });

    // Date validation
    if (formData.effective_date && formData.expiration_date) {
      if (
        new Date(formData.effective_date) > new Date(formData.expiration_date)
      ) {
        errors.expiration_date = "Expiration date must be after effective date";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add New Routing Guide</DialogTitle>
      <DialogContent>
        <TextField
          required
          error={!!formErrors.guide_name}
          helperText={formErrors.guide_name}
          label="Guide Name"
          fullWidth
          margin="normal"
          name="guide_name"
          value={formData.guide_name}
          onChange={handleChange}
        />

        <TextField
          label="Supplier Site ID"
          fullWidth
          margin="normal"
          name="supplier_site_id"
          value={formData.supplier_site_id}
          onChange={handleChange}
        />
        <FormControl
          fullWidth
          margin="normal"
          required
          error={!!formErrors.division_id}
        >
          <InputLabel id="division-label">Division</InputLabel>
          <Select
            labelId="division-label"
            name="division_id"
            value={formData.division_id}
            onChange={handleChange}
            label="Division"
          >
            {Division.map((code) => (
              <MenuItem key={code.id} value={code.id}>
                {code.value}
              </MenuItem>
            ))}
          </Select>
          {formErrors.label && (
            <FormHelperText>{formErrors.label}</FormHelperText>
          )}
        </FormControl>

        <TextField
          label="Vendor Name"
          fullWidth
          margin="normal"
          name="vendor_name"
          value={formData.vendor_name}
          onChange={handleChange}
        />

        <FormControl
          fullWidth
          margin="normal"
          required
          error={!!formErrors.commodity_code_id}
        >
          <InputLabel id="commodity-code-label">Commodity Code</InputLabel>
          <Select
            labelId="commodity-code-label"
            name="commodity_code_id"
            value={formData.commodity_code_id}
            onChange={handleChange}
            label="Commodity Code"
          >
            {commodityCodes.map((code) => (
              <MenuItem
                key={code.commodity_code_id}
                value={code.commodity_code_id}
              >
                {code.commodity_code}-{code.name}
              </MenuItem>
            ))}
          </Select>
          {formErrors.commodity_code_id && (
            <FormHelperText>{formErrors.commodity_code_id}</FormHelperText>
          )}
        </FormControl>

        <TextField
          required
          error={!!formErrors.freight_terms}
          helperText={formErrors.freight_terms}
          label="Freight Terms"
          fullWidth
          margin="normal"
          name="freight_terms"
          value={formData.freight_terms}
          onChange={handleChange}
        />

        <TextField
          required
          error={!!formErrors.current_version}
          helperText={formErrors.current_version}
          label="Current Version"
          fullWidth
          margin="normal"
          name="current_version"
          value={formData.current_version}
          onChange={handleChange}
        />

        <TextField
          label="Previous Version"
          fullWidth
          margin="normal"
          name="prev_version"
          value={formData.prev_version}
          onChange={handleChange}
        />

        <TextField
          required
          error={!!formErrors.publish_date}
          helperText={formErrors.publish_date}
          label="Publish Date"
          type="date"
          fullWidth
          margin="normal"
          name="publish_date"
          value={formData.publish_date}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          required
          error={!!formErrors.effective_date}
          helperText={formErrors.effective_date}
          label="Effective Date"
          type="date"
          fullWidth
          margin="normal"
          name="effective_date"
          value={formData.effective_date}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          required
          error={!!formErrors.expiration_date}
          helperText={formErrors.expiration_date}
          label="Expiration Date"
          type="date"
          fullWidth
          margin="normal"
          name="expiration_date"
          value={formData.expiration_date}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label="Comments"
          fullWidth
          margin="normal"
          name="comments"
          value={formData.comments}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit}>Add</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoutingGuideForm;
